import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ChatWidgetState {
  isOpen: boolean;
  solicitudId: string | null;
  usuarioId: string | null;
  chatId: string | null;
}

const STORAGE_KEY = 'ANALYSIS_CHAT_STATE';

@Injectable({
  providedIn: 'root'
})
export class AnalysisChatStateService {
  private initialState: ChatWidgetState = {
    isOpen: false,
    solicitudId: null,
    usuarioId: null,
    chatId: null
  };

  private stateSubject: BehaviorSubject<ChatWidgetState>;
  state$;

  constructor() {
    const restored = this.loadFromStorage();
    this.stateSubject = new BehaviorSubject<ChatWidgetState>(restored || this.initialState);
    this.state$ = this.stateSubject.asObservable();
  }

  get currentState(): ChatWidgetState {
    return this.stateSubject.value;
  }

  openChat(solicitudId: string, usuarioId: string, chatId?: string): void {
    const state: ChatWidgetState = {
      isOpen: true,
      solicitudId,
      usuarioId,
      chatId: chatId || null
    };
    this.updateState(state);
  }

  setChatId(chatId: string): void {
    const state = { ...this.stateSubject.value, chatId };
    this.updateState(state);
  }

  closeChat(): void {
    this.updateState({
      ...this.stateSubject.value,
      isOpen: false
    });
  }

  toggleChat(): void {
    const current = this.stateSubject.value;
    this.updateState({
      ...current,
      isOpen: !current.isOpen
    });
  }

  resetChat(): void {
    this.updateState(this.initialState);
    this.clearStorage();
  }

  get isActive(): boolean {
    return this.stateSubject.value.solicitudId !== null;
  }

  private updateState(state: ChatWidgetState): void {
    this.stateSubject.next(state);
    this.saveToStorage(state);
  }

  private saveToStorage(state: ChatWidgetState): void {
    try {
      const toSave = {
        solicitudId: state.solicitudId,
        usuarioId: state.usuarioId,
        chatId: state.chatId,
        isOpen: state.isOpen
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch { /* silencioso */ }
  }

  private loadFromStorage(): ChatWidgetState | null {
    // No restaurar estado al recargar la página.
    // El chat solo se abre cuando el usuario crea una solicitud nueva.
    this.clearStorage();
    return null;
  }

  private clearStorage(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* silencioso */ }
  }
}
